/**
 * Custom Grid Video Autoplay for Impact Theme
 * Enables viewport-based autoplay for native video elements in product gallery grid layout
 * 
 * Version: 3.0 - Native video bypass
 * Dependencies: None (vanilla JS)
 * 
 * How it works:
 * - When enabled, grid videos render as native <video> instead of <video-media> component
 * - Bypasses the component's overlay that blocks video visibility
 * - Uses IntersectionObserver to play videos when ≥50% visible
 * - Handles re-initialization on variant changes and section reloads
 */

(function() {
  'use strict';

  // Track unmuted videos to avoid re-muting during session
  const unmutedVideos = new WeakSet();
  
  // Store observer instance for cleanup
  let observer = null;
  let observedVideos = [];

  /**
   * Initialize autoplay functionality
   */
  function initGridAutoplay() {
    // Exit if autoplay is not enabled via data attribute
    const autoplayContainer = document.querySelector('[data-autoplay-videos="true"]');
    if (!autoplayContainer) {
      return;
    }

    // Clean up existing observer if present
    cleanupObserver();

    // Query all native video elements in media grid tiles
    const videos = document.querySelectorAll('.product-gallery__media[data-media-type="video"] video');
    
    if (videos.length === 0) {
      return;
    }

    // Normalize video attributes for autoplay compatibility
    videos.forEach(video => {
      // Set properties directly (required for WebKit/iOS)
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.loop = true;
      video.controls = false;  // Remove controls to prevent theme pause logic
      
      // Also set attributes for compatibility
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
      video.removeAttribute('controls');  // Strip controls attribute
      
      // Keep preload="metadata" for performance
      if (!video.hasAttribute('preload')) {
        video.setAttribute('preload', 'metadata');
      }
      
      // Track volumechange to detect user unmuting
      video.addEventListener('volumechange', handleVolumeChange);
    });

    // Create IntersectionObserver with multiple thresholds for better visibility detection
    observer = new IntersectionObserver(handleIntersection, {
      root: null,  // Use window/viewport
      rootMargin: '0px 0px -10% 0px',  // Slight bottom margin to ensure videos are truly visible
      threshold: [0, 0.5, 1]  // Fire at multiple visibility points
    });


    // Observe all videos
    videos.forEach(video => {
      observer.observe(video);
      observedVideos.push(video);
    });
  }

  /**
   * Handle intersection changes for videos
   */
  function handleIntersection(entries) {
    entries.forEach(entry => {
      const video = entry.target;
      
      // Play when at least 50% visible, pause otherwise
      if (entry.intersectionRatio >= 0.5) {
        playVideo(video);
      } else {
        pauseVideo(video);
      }
    });
  }

  /**
   * Safely attempt to play video
   */
  function playVideo(video) {
    // Ensure video stays muted unless user has unmuted it
    if (!unmutedVideos.has(video)) {
      video.muted = true;
    }
    
    // Attempt to play video
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Silently ignore autoplay rejections (e.g., low-power mode)
      });
    }
  }

  /**
   * Safely pause video
   */
  function pauseVideo(video) {
    try {
      video.pause();
      // Optional: Reset to beginning when paused
      // Uncomment next line if you want videos to restart from beginning
      // video.currentTime = 0;
    } catch (e) {
      // Silently ignore pause errors
    }
  }

  /**
   * Track when user manually unmutes a video
   */
  function handleVolumeChange(event) {
    const video = event.target;
    if (!video.muted) {
      // User has unmuted - track this video to avoid re-muting
      unmutedVideos.add(video);
    }
  }

  /**
   * Clean up existing observer and event listeners
   */
  function cleanupObserver() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    
    // Remove event listeners from previously observed videos
    observedVideos.forEach(video => {
      video.removeEventListener('volumechange', handleVolumeChange);
    });
    
    observedVideos = [];
  }

  /**
   * Initialize on DOM ready
   */
  function domReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  // Initialize on DOM ready
  domReady(initGridAutoplay);

  // Re-initialize on Impact theme variant change event
  document.addEventListener('variant:change', function() {
    setTimeout(initGridAutoplay, 100); // Small delay to ensure DOM is updated
  });

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function(event) {
    // Only re-init if the loaded section contains our product gallery
    if (event.detail.sectionId && event.target.querySelector('.product-gallery')) {
      setTimeout(initGridAutoplay, 100);
    }
  });

  // Clean up on section unload (theme editor)
  document.addEventListener('shopify:section:unload', function() {
    cleanupObserver();
  });

})();